import {confirmSkip} from './_helpers/skip-confirmation';
import {isChunkLoadError, reloadIntoCurrentBuild} from './_helpers/chunk-load-recovery';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  RendererStyleFlags2,
  ViewChild,
} from '@angular/core';
import {Location} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {Router, ActivatedRoute, GuardsCheckEnd, NavigationEnd, NavigationError} from '@angular/router';
import {AuthenticationService} from '@app/services/authentication.service';
import {User} from '@app/modules/shared/models';

import {select, Store} from '@ngrx/store';
import {
  getLoggedInUser,
  IAuthenticationState,
  IProjectState,
  EndImpersonation,
  getIsImpersonating,
  getCreateOrCopySuccess,
  getProjectError,
  TokenLogin,
  UploadToSignedUrl,
  IFileUploadState,
  getFileUploadProgress,
  getUploadToSignedUrl,
  ITemplateState,
  getTemplateSubTypes,
  LogoutSuccess
} from '@app/stores';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {LoaderService} from '@app/services/loader.service';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';
import {TokenRefreshComponent, TokenRefreshResult} from '@app/_components/token-refresh/token-refresh.component';
import {
  isMfaAdvisoryDue,
  isMfaNudgeDue,
  localDateKey,
  MFA_NUDGE_DISMISSED_KEY,
  mfaAdvisoryDismissKey,
  MfaNudgeModalComponent
} from '@app/_components/mfa-nudge-modal/mfa-nudge-modal.component';
import {SocketService} from '@app/services/socket.service';
import {ToastrService} from 'ngx-toastr';
import {ReportModel, TemplateSubType} from "@app/modules/shared/models";
import {versionInfo} from '../../version-info';
import {AwsService} from '@app/services/aws.service';
import {ThemeService} from '@app/services/theme.service';
import {CkeditorThemeService} from '@app/services/ckeditor-theme.service';
import {UserAbilityService} from '@app/services/user-ability.service';
import {TaskNotificationService} from '@app/services/task-notification.service';
import {
  SessionExpiryWarning,
  SessionTimeoutReason,
  SessionTimeoutService
} from '@app/services/session-timeout.service';
import {AppVersionService} from '@app/services/app-version.service';
import {NavLayoutService} from '@app/services/nav-layout.service';
import {NavigationMenuService} from '@app/services/navigation-menu.service';
import {ProjectContextService} from '@app/services/project-context.service';
import {MaintenanceService} from '@app/services/maintenance.service';

@Component({
  selector: 'app-component',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy, OnInit, AfterViewInit {
  currentUser: any;

  isImpersonating = false;
  mfaAdvisory: any = null;
  passwordExpiryWarning: {expires_at: string, reason?: string} | null = null;
  sessionExpiryWarning: SessionExpiryWarning | null = null;
  /** The MFA nudge opens at most once per app load; the daily gate lives in localStorage. */
  private mfaNudgeShown = false;
  private sessionModalRef: NgbModalRef | null = null;
  /** Set when the user closed the countdown modal by hand; cleared with the warning. */
  private sessionModalSuppressed = false;
  isInfluencer: boolean;
  projectId: number;
  unsubscriber = new Subject();
  subscription: any;
  eventInit = false;
  currentRoute = '/';
  public report: ReportModel = null;
  fileName: any;
  fileResponse: any[] = [];

  blobObject: any;

  templateSubTypes: TemplateSubType[] = [];

  @ViewChild('appAlertStack', {static: true}) appAlertStack?: ElementRef<HTMLElement>;

  private alertResizeObserver?: ResizeObserver;

  /** Role default landing (logo / home), from login API or Ability map. */
  get homeRoute(): string {
    return this.userAbilityService.getDefaultDashboard(this.currentUser);
  }

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private authenticationService: AuthenticationService,
    private projectStore: Store<IProjectState>,
    private authenticationStore: Store<IAuthenticationState>,
    private templateStore: Store<ITemplateState>,
    private sbModal: SbModalService,
    private socketService: SocketService,
    public loaderService: LoaderService,
    private toastr: ToastrService,
    private awsService: AwsService,
    private fileUploadStore: Store<IFileUploadState>,
    private themeService: ThemeService,
    private ckeditorThemeService: CkeditorThemeService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private userAbilityService: UserAbilityService,
    private taskNotificationService: TaskNotificationService,
    private sessionTimeoutService: SessionTimeoutService,
    private appVersionService: AppVersionService,
    private navLayout: NavLayoutService,
    private navigationMenu: NavigationMenuService,
    private projectContext: ProjectContextService,
    public maintenance: MaintenanceService,
  ) {
    this.themeService.loadTheme();
    this.ckeditorThemeService.start();
    this.logOutEventListener = this.logOutEventListener.bind(this);
    translate.setDefaultLang('en');
    const browserLang = translate.getBrowserLang();
    translate.use(browserLang === 'fr' ? 'fr' : 'en');
    this.subscribeStores();
    this.projectContext.context$.pipe(takeUntil(this.unsubscriber)).subscribe((context) => {
      this.projectId = context.projectId;
      this.taskNotificationService.setProjectId(context.projectId);
    });
    this.sessionTimeoutService.warning$.pipe(takeUntil(this.unsubscriber))
      .subscribe((warning) => this.sessionExpiryWarning = warning);
    this.sessionTimeoutService.expired$.pipe(takeUntil(this.unsubscriber))
      .subscribe((reason) => this.handleSessionTimeout(reason));
    // To connect socket on each page
    this.subscription = this.socketService.sourceData
      .subscribe((message: any) => {});
    router.events.subscribe(e => {
      if (e instanceof GuardsCheckEnd && e.shouldActivate &&
        this.appVersionService.loadRequired(e.urlAfterRedirects)) {
        // The server no longer lets a tab run this release; the page change its guards have
        // just allowed loads the current build instead.
        return;
      }
      if (e instanceof NavigationError && isChunkLoadError(e.error)) {
        // This tab's build refers to lazy chunks a deploy has since removed; a full page load
        // of the target picks up the current build.
        if (!reloadIntoCurrentBuild(e.url)) {
          this.appVersionService.markStale();
          this.toastr.warning('This page could not be loaded. Please refresh to get the latest version of Snöball.');
        }
        return;
      }
      if (e instanceof NavigationEnd) {
        if (!this.router.url.includes('/login')) {
          this.currentRoute = this.router.url;
        }
        if (this.mfaAdvisory && !this.mfaAdvisory.dismissible
          && !this.router.url.startsWith('/users/security')) {
          this.router.navigate(['/users', 'security']);
          return;
        }
        this.checkAndRedirectIfUserWithNoClient();
        this.checkAndRedirectIfUserGoingToClientRoute();
        const path = this.location.path().replace(/\?.*$/, '');

        // Keep top nav visible for authenticated app routes. Public front routes hide it themselves.
        if (this.currentUser && !path.startsWith('/login') && !path.startsWith('/front')) {
          this.loaderService.showNavbarSubject.next(true);
        }

        if (!this.eventInit && localStorage.getItem('currentUser')) {
          window.addEventListener('storage', this.logOutEventListener, false);
        }
      }
    });
  }

  checkAndRedirectIfUserWithNoClient() {
    if (this.currentUser) {
      const canViewAdminFeatures = this.currentUser.can('view-admin-features', 'navigation');
      if (!canViewAdminFeatures && !this.currentUser.client_id && !this.currentUser.organization_id) {
        this.toastr.warning('Please select a client');
        this.router.navigate(['/users', 'profile', this.currentUser.id]);
      }
    }
  }

  checkAndRedirectIfUserGoingToClientRoute() {
    if (this.currentUser) {
      const canViewClientsMenu = this.currentUser.can('view-clients-menu', 'navigation');
      if (!canViewClientsMenu) {
        const path = this.location.path().replace(/\?.*$/, '');
        if (path.startsWith('/clients')) {
          this.router.navigate(['/projects']);
        }
      }
    }
  }

  subscribeStores() {
    this.templateStore.pipe(select(getTemplateSubTypes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(templateSubTypes => {
        if (templateSubTypes && templateSubTypes.length > 0) {
          this.templateSubTypes = templateSubTypes;
        }
      });

    this.authenticationStore.pipe(select(getIsImpersonating))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(impersonating => this.isImpersonating = impersonating);

    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          const userModel = Object.assign(new User(), user);
          if (localStorage.getItem('resetUser') !== null && localStorage.getItem('currentUser') !== null) {
            localStorage.removeItem('resetUser');
          }
          if (localStorage.getItem('resetUser') === null) {
            this.currentUser = userModel;
            this.passwordExpiryWarning = userModel.password_expiry_warning || null;
            this.loadMfaAdvisory();
            this.userAbilityService.setUser(userModel);
            this.checkAndRedirectIfUserWithNoClient();
            this.isInfluencer = this.currentUser.user_type === '2';
            this.navLayout.setAuthenticated(true);
            if (!this.isInfluencer) {
              this.taskNotificationService.setProjectId(this.projectId);
              this.taskNotificationService.start(this.currentUser.id);
            }
          } else {
            this.currentUser = null;
            this.passwordExpiryWarning = null;
            this.navLayout.setAuthenticated(false);
            this.sessionTimeoutService.clear();
            this.taskNotificationService.stop();
          }
        } else {
          this.currentUser = null;
          this.passwordExpiryWarning = null;
          this.navLayout.setAuthenticated(false);
          this.sessionTimeoutService.clear();
          this.taskNotificationService.stop();
        }
      });


    this.projectStore.pipe(select(getProjectError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });
    // this.projectStore.pipe(select(getProjectSuccess))
    //   .pipe(takeUntil(this.unsubscriber))
    //   .subscribe(success => {
    //     if (success) {
    //       this.projects = this.projectList;
    //     }
    //   });

    this.projectStore.pipe(select(getCreateOrCopySuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(createOrCopySuccess => {
        if (createOrCopySuccess) {
          // this.projectStore.dispatch(ProjectListAll({
          //   params: {
          //     options: JSON.stringify({include_influencers_count: true, include_totals: true})
          //   }
          // }));
        }
      });

    this.fileUploadStore.pipe(select(getFileUploadProgress))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((data: any) => {
        // if (data !== undefined && data !== null) {
        //   this.importProgress = {
        //     status: 'uploading',
        //     progress: data
        //   };
        //   if (data >= 100) {
        //     this.importProgress = {
        //       status: 'started',
        //       progress: 0
        //     };
        //   }
        // }
      });

    this.fileUploadStore.pipe(select(getUploadToSignedUrl))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((data: any) => {
        if (data && this.fileResponse && this.fileResponse.length > 0) {
          // this.csvUrl = this.fileResponse[0]['url'].split("?")[0];
          // const a = document.createElement("a");
          // a.href = window.URL.createObjectURL(this.blobObject);
          // a.download = this.fileName;
          // a.click();
          // // this.exportModelRef.close();
          // this.csvDownloadComplete = true;
          // this.exportProgress.status = 'started';
          // this.exportProgress.progress = '0';
        }
      });
  }

  private loadMfaAdvisory(): void {
    this.authenticationService.getEffectivePolicy().pipe(takeUntil(this.unsubscriber))
      .subscribe({
        next: (response: any) => {
          const advisory = response?.data?.mfa;
          const dismissed = localStorage.getItem('mfaAdvisoryDismissedFor');
          this.mfaAdvisory = isMfaAdvisoryDue(advisory)
            && (!advisory.dismissible || dismissed !== mfaAdvisoryDismissKey(advisory))
            ? advisory
            : null;
          if (this.mfaAdvisory && !this.mfaAdvisory.dismissible
            && !this.router.url.startsWith('/users/security')) {
            this.router.navigate(['/users', 'security']);
          }
          this.maybeShowMfaNudge(advisory);
        },
        error: () => this.mfaAdvisory = null
      });
  }

  /**
   * Daily enforcement nudge, independent of the inline banner's per-deadline dismissal.
   * Opens once per app load and, while the server still allows deferring, only when
   * today's local date differs from the stored "Remind me later" date.
   */
  private maybeShowMfaNudge(advisory: any): void {
    // `reminderDue`, not `enforcementScheduled`: with two-factor required by default
    // there is no announced date to schedule against, and gating on one meant the popup
    // never opened - so the person was pushed into forced enrolment having never been
    // offered the postponement they are entitled to. The server owns the whole rule,
    // including whether a week's postponement is already running.
    if (!advisory?.reminderDue || this.mfaNudgeShown || !this.currentUser) return;
    const dismissible = advisory.dismissible !== false;
    if (dismissible && !isMfaNudgeDue(this.readStorage(MFA_NUDGE_DISMISSED_KEY))) return;
    this.mfaNudgeShown = true;
    const modal = this.sbModal.open({
      title: this.translate.instant('AuthSecurity.mfa_nudge_title'),
      size: 'md',
      bodyComponent: MfaNudgeModalComponent,
      modalData: {advisory},
      showFooter: false,
      backdrop: dismissible ? true : 'static',
      keyboard: dismissible
    });
    modal.result.then(
      async (result) => {
        if (result === 'defer') {
          const agreed = await confirmSkip(this.sbModal, this.translate, 'mfa', {
            days: advisory.reminderDeferralDays,
            skipsLeft: advisory.reminderDeferralsLeft
          });
          if (agreed) this.deferMfaReminder();
          return;
        }
        this.router.navigate(['/users', 'security']);
      },
      () => {
        if (dismissible) {
          this.writeStorage(MFA_NUDGE_DISMISSED_KEY, localDateKey());
        } else if (!this.router.url.startsWith('/users/security')) {
          this.router.navigate(['/users', 'security']);
        }
      }
    );
  }

  /**
   * Spend the one postponement this account gets. The banner goes with it, because the
   * whole point is a week without being asked; a refusal leaves both in place and says
   * why, since the only reasons are that it was already used or the deadline is close.
   */
  private deferMfaReminder(): void {
    this.authenticationService.deferMfaReminder()
      .pipe(takeUntil(this.unsubscriber))
      .subscribe({
        next: () => {
          this.mfaAdvisory = null;
          this.toastr.success(
            this.translate.instant('AuthSecurity.mfa_nudge_skipped')
          );
        },
        error: () => this.toastr.error(
          this.translate.instant('AuthSecurity.mfa_nudge_skip_failed')
        )
      });
  }

  private readStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  private writeStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // Storage unavailable (private mode / quota): the nudge simply shows again next load.
    }
  }

  /**
   * Whether there is a date to count down to. Two-factor is required by default and usually
   * has none, and ngx-translate leaves a `{{days}}` placeholder verbatim when its value is null.
   */
  get mfaAdvisoryHasDeadline(): boolean {
    return Number(this.mfaAdvisory?.daysRemaining ?? 0) > 0;
  }

  dismissMfaAdvisory(): void {
    if (!this.mfaAdvisory?.dismissible) return;
    localStorage.setItem('mfaAdvisoryDismissedFor', mfaAdvisoryDismissKey(this.mfaAdvisory));
    this.mfaAdvisory = null;
  }

  openSecurity(): void {
    this.router.navigate(['/users', 'security']);
  }

  openPasswordSettings(): void {
    if (!this.currentUser?.id) return;
    this.router.navigate(['/users', 'profile', this.currentUser.id]);
  }

  formatSessionRemaining(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  }

  /**
   * Countdown modal for the idle/absolute session-timeout warning. The body component
   * re-renders the seconds itself from warning$; this only opens the modal on the first
   * emission of a warning window and closes it when the warning clears (null).
   */
  private syncSessionExpiryModal(warning: SessionExpiryWarning | null): void {
    if (!warning || !this.currentUser) {
      this.sessionModalSuppressed = false;
      this.closeSessionExpiryModal();
      return;
    }
    if (this.sessionModalRef || this.sessionModalSuppressed) return;
    const modal = this.sbModal.open({
      title: this.translate.instant('AuthSecurity.session_expiring_title'),
      size: 'md',
      bodyComponent: TokenRefreshComponent,
      showFooter: false,
      backdrop: 'static',
      keyboard: false
    });
    this.sessionModalRef = modal;
    modal.result.then(
      (result: TokenRefreshResult) => {
        this.sessionModalRef = null;
        // The refresh response re-arms the service's timers but does not emit null, so
        // drop the stale banner countdown here.
        if (result === 'refreshed') this.sessionExpiryWarning = null;
      },
      () => {
        // Closed via the header "x": stay quiet until this warning window clears.
        this.sessionModalRef = null;
        this.sessionModalSuppressed = true;
      }
    );
  }

  private closeSessionExpiryModal(): void {
    if (!this.sessionModalRef) return;
    const modal = this.sessionModalRef;
    this.sessionModalRef = null;
    modal.close();
  }

  private handleSessionTimeout(reason: SessionTimeoutReason): void {
    this.closeSessionExpiryModal();
    if (!this.currentUser) return;
    const messageKey = reason === 'absolute_timeout'
      ? 'AuthSecurity.absolute_timeout'
      : 'AuthSecurity.idle_timeout';
    this.toastr.error(this.translate.instant(messageKey));
    this.loaderService.unsubscribe();
    this.authenticationService.setAccessToken(null);
    this.authenticationStore.dispatch(LogoutSuccess());
    localStorage.removeItem('currentUser');
    localStorage.removeItem('admin_currentUser');
    this.router.navigate(['/login'], {queryParams: {returnUrl: this.currentRoute}});
  }

  ngOnInit() {
    this.maintenance.active$.pipe(takeUntil(this.unsubscriber)).subscribe(active => {
      if (active) {
        this.sessionTimeoutService.clear();
        this.closeSessionExpiryModal();
      }
    });
    this.maintenance.start();
    if (localStorage.getItem('currentUser') && !this.authenticationService.getAccessToken()) {
      this.authenticationStore.dispatch(TokenLogin({token: '', restore: true}));
    }
    this.sessionTimeoutService.warning$.pipe(takeUntil(this.unsubscriber))
      .subscribe((warning) => this.syncSessionExpiryModal(warning));
    if (!this.eventInit && localStorage.getItem('currentUser')) {
      window.addEventListener('storage', this.logOutEventListener, false);
    }
  }

  ngAfterViewInit(): void {
    const footer = this.elementRef.nativeElement.querySelector('.footer');
    if (footer) {
      const comment = this.renderer.createComment(
        ` commit: ${versionInfo.build_number} date: ${versionInfo.build_date} `
      );
      this.renderer.insertBefore(footer, comment, footer.firstChild);
    }

    if (typeof window !== 'undefined' && typeof ResizeObserver !== 'undefined') {
      this.alertResizeObserver = new ResizeObserver(() => this.updateAlertStackHeight());
      if (this.appAlertStack?.nativeElement) {
        this.alertResizeObserver.observe(this.appAlertStack.nativeElement);
      }
    }

    this.updateAlertStackHeight();
  }

  /** The skip link: past the menus to the page, without changing the URL. */
  skipToMain(event: Event): void {
    event.preventDefault();
    document.getElementById('sn-main')?.focus();
  }

  private updateAlertStackHeight(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const height = Math.ceil(this.appAlertStack?.nativeElement.getBoundingClientRect().height || 0);
    this.renderer.setStyle(
      document.documentElement,
      '--sn-alert-stack-height',
      `${height}px`,
      RendererStyleFlags2.DashCase
    );
  }


  getSignedUrlFromFile(file) {
    return new Promise((resolve, reject) => this.awsService.getAwsSignedUrl(file.name, file.type, true).subscribe((resp: any) => {
      resolve({
        url: resp.data.url,
        fileData: file
      });
    }, reject));
  }

  switchLanguage(language: string) {
    this.translate.use(language);
  }

  logout() {
    this.navigationMenu.logout();
  }

  get impersonatedName(): string | null {
    return this.isImpersonating && this.currentUser ? this.getNameOrEmail() : null;
  }

  revertImpersonation() {
    this.authenticationStore.dispatch(EndImpersonation());
  }

  getNameOrEmail() {
    if (this.currentUser) {
      if(this.currentUser.first_name || this.currentUser.last_name) {
        return `${this.currentUser.first_name} ${this.currentUser.last_name}`
      }
      return this.currentUser.email;
    }
  }

  ngOnDestroy() {
    this.maintenance.stop();
    this.sessionTimeoutService.clear();
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
    this.subscription.unsubscribe();
    this.alertResizeObserver?.disconnect();
    this.renderer.removeStyle(
      document.documentElement,
      '--sn-alert-stack-height',
      RendererStyleFlags2.DashCase
    );

    try {
      window.removeEventListener('storage', this.logOutEventListener);
      this.eventInit = false;
    } catch (e) {}
  }

  logOutEventListener(event) {
    this.eventInit = true;
    if (event.storageArea === localStorage) {
      const currentUser = localStorage.getItem('currentUser');
      if (!currentUser) {
        window.removeEventListener('storage', this.logOutEventListener);
        this.eventInit = false;
        window.location.reload();
      }
    }
  }
}
